/**
 * Booking Detail Modal Component
 *
 * Charter: documentation/cursor/Charters/CodeAuditor.md v1.0.1
 * Spec: documentation/cursor/owner-dashboard-ui-specification-v1-final.md §4 Component 10
 *
 * Purpose:
 *   Owner-portal modal for a single reservation: header (guest, reservation #, channel, dates, status),
 *   financial folio (guest payment + owner payout), inline Reviews/Notes/Files panels, OwnerStay card,
 *   Stay Details, Admin Parity, Comments. Desktop shows inline folio; mobile uses BottomSheet + FinancialDetailsModal.
 *
 * Workflow:
 *   1. Data fetch: detail (reservation by id), notes, reviews, files (has-files + list when panel opens), links (if feature enabled).
 *   2. Canonical/persisted: detailData.canonical_financial_summary || booking.canonical_financial_summary for channel/status;
 *      detailData.financials_persisted_summary is source of truth for financial numbers (no fallback to canonical).
 *   3. Render: loading/error/empty states; then folio sections gated by isOwnerStay, channel-type, and payout_method.
 *
 * Assumptions & Invariants:
 *   - Owner-safe: all data is for the authenticated owner; server enforces. UI gating is convenience only.
 *   - Canonical precedence: forced lease (channel_subtype clc_lease / payout_method clc_lease_computed) overrides OwnerStay.
 *   - Persisted-only for financials: when persisted_available is false, show empty state; do not fall back to canonical/MV for numbers.
 *
 * Dependencies:
 *   - Internal: BottomSheet, FinancialDetailsModal, CommentThread; sourceColors, reservationFinancials, masterChannelResolver,
 *     channelColorUtils, dateTimeET, ownerApi, ownerFeatures.
 *   - Context: ThemeContext, SessionContext (isAdminUser for Admin Parity).
 *
 * Version: 1.1.0
 *
 * Changelog:
 *   2026-01-27 Phase 1 docs-only (CodeAuditor charter): added Purpose, Workflow, Assumptions, Dependencies, Version.
 *   2026-01-27 owner_reservation_financials_files_icon_fix:
 *     - Files icon always in header toolbar (with Notes/Reviews); blue dot only when reservation-linked files exist.
 *     - Removed Financials Compare icon, drawer, state, and all related code.
 *   2026-01-27 owner_reservation_files_inline_panel_fix:
 *     - Files panel now opens inline (matching Reviews/Notes pattern) instead of bottom drawer.
 *     - Empty state shows "No files are available for this reservation." (no error when empty).
 *     - Removed ReservationFilesDrawer usage from BookingDetailModal.
 *   2026-01-27 owner_guest_payment_nightly_display:
 *     - Guest Payment primary line: show "$X × Y nights" when nights valid (X = base_rate/nights only), else "Paid by Guest".
 *     - Section header "Total Guest Payment". Per-night from base rate only; no cleaning, taxes, fees.
 *   2026-01-28 guest_payment_header_cleanup:
 *     - Section title "Guest Payment"; primary row label "Total Guest Payment" (no "$X × N nights" in header).
 *     - "$X × N nights" shown only under Base Rate line item.
 *   2026-01-27 owner_files_panel_fetch_fix:
 *     - Files panel parse: accept { data.links }, { links }, or direct array (backend sendOwnerSuccess puts links at top level).
 *     - 403/404 already treated as empty in .catch(); no change.
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Edit3, Trash2 } from 'lucide-react';
import FinancialDetailsModal from './FinancialDetailsModal.jsx';
import OwnerStaySection from './booking-detail/OwnerStaySection.jsx';
import VrboSection, { getVrboPayoutTooltipText } from './booking-detail/VrboSection.jsx';
import BookingEngineSection, { getBookingEnginePayoutTooltipText } from './booking-detail/BookingEngineSection.jsx';
import ClcLeaseSection, { getClcLeasePayoutTooltipText } from './booking-detail/ClcLeaseSection.jsx';
import AirbnbSection, { getAirbnbPayoutTooltipText } from './booking-detail/AirbnbSection.jsx';
import DefaultPayoutSection from './booking-detail/DefaultPayoutSection.jsx';
import BookingFinancialFolio from './booking-detail/BookingFinancialFolio.jsx';
import BookingDetailHeader from './booking-detail/BookingDetailHeader.jsx';
import BookingDetailModalShell from './booking-detail/BookingDetailModalShell.jsx';
import NotesPanel from './booking-detail/panels/NotesPanel.jsx';
import ReviewsPanel from './booking-detail/panels/ReviewsPanel.jsx';
import FilesPanel from './booking-detail/panels/FilesPanel.jsx';
import LinksPanel from './booking-detail/panels/LinksPanel.jsx';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { useSession } from '../../contexts/SessionContext.jsx';
import CommentThread from './CommentThread.jsx';
import { getSourceChannelDisplay } from '../../utils/sourceColors.js';
import { resolveMasterChannel } from '../../utils/masterChannelResolver.js';
import { getChannelColorVar, getChannelColorHex } from '../../utils/channelColorUtils.js';
import { formatDateOnlyET, formatDateTimeET, formatDateRangeET, normalizeToYmd } from '../../utils/dateTimeET.js';
import { getReservationNotes, getReservationReviews, getReservationLinks, getReservationHasFiles, getReservationDetail } from '../../services/ownerApi.js';
import { useBookingReviews } from '../../hooks/booking-detail/useBookingReviews.js';
import { useBookingNotes } from '../../hooks/booking-detail/useBookingNotes.js';
import { useBookingFiles } from '../../hooks/booking-detail/useBookingFiles.js';
import { useBookingLinks } from '../../hooks/booking-detail/useBookingLinks.js';
import { useBookingDetailData } from '../../hooks/booking-detail/useBookingDetailData.js';
import { useTooltipPortal } from '../../hooks/useTooltipPortal.js';
import TooltipPortal from './booking-detail/TooltipPortal.jsx';
import { OWNER_FEATURES } from '../../config/ownerFeatures.js';
import {
  resolveCanonical,
  getDaysUntilCheckIn,
  isForcedLease as checkForcedLease,
  isOwnerStay as checkOwnerStay,
  isLegacyVRBOiCal as checkLegacyVRBOiCal,
  isVRBONonICalComputed as checkVRBONonICalComputed,
  isVRBOStripe as checkVRBOStripe,
  isCLCLease as checkCLCLease,
  isBookingEngine as checkBookingEngine,
  isDirect as checkDirect,
  isHomeAway as checkHomeAway,
  isLeaseStrict as checkLeaseStrict,
  isCLC as checkCLC,
} from '../../utils/bookingDetailModalUtils.js';

/**
 * Single pure helper for Owner Payout info-icon tooltip. Used by both onMouseEnter and onFocus.
 * Branch order and strings match prior inline logic; delegates to channel helpers.
 * Integrated VRBO (non-iCal): time-based rule via daysUntilCheckIn (>30 vs ≤30); legacy iCal/Stripe unchanged.
 */
function getOwnerPayoutTooltipText({
  isFinalCopy,
  isLegacyVRBOiCal,
  isVRBONonICalComputed,
  isVRBOStripe,
  isIntegratedVrboNonIcal,
  daysUntilCheckIn,
  isCLCLease,
  isBookingEngine,
  isDirect,
  isHomeAway,
}) {
  if (isFinalCopy) {
    if (isCLCLease) return getClcLeasePayoutTooltipText(true, isCLCLease);
    if (isVRBONonICalComputed || isLegacyVRBOiCal || isVRBOStripe) return getVrboPayoutTooltipText(true, isLegacyVRBOiCal, isVRBONonICalComputed, isVRBOStripe, daysUntilCheckIn, isIntegratedVrboNonIcal);
    if (isBookingEngine || isDirect || isHomeAway) return getBookingEnginePayoutTooltipText(true, isBookingEngine, isDirect, isHomeAway);
    return getAirbnbPayoutTooltipText(true, true);
  }
  if (isCLCLease) return getClcLeasePayoutTooltipText(false, isCLCLease);
  if (isLegacyVRBOiCal || isVRBONonICalComputed || isVRBOStripe) return getVrboPayoutTooltipText(false, isLegacyVRBOiCal, isVRBONonICalComputed, isVRBOStripe, daysUntilCheckIn, isIntegratedVrboNonIcal);
  if (isBookingEngine || isDirect || isHomeAway) return getBookingEnginePayoutTooltipText(false, isBookingEngine, isDirect, isHomeAway);
  return getAirbnbPayoutTooltipText(false, true);
}

// Format currency helper (defined outside component to avoid initialization issues)
const formatCurrency = (value) => {
  if (!value) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

// Format currency with refined presentation (for hero amounts - display only, no calculations)
const formatCurrencyRefined = (value) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }
  // If string, try to parse and format
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    if (!isNaN(parsed)) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(parsed);
    }
  }
  return String(value);
};

// CLC Fee percent: display 12% not 0.12%. If <=1 treat as fraction (×100), else as already-percent; max 1 decimal.
const formatClcFeePercentForDisplay = (p) => {
  if (p == null || (typeof p !== 'number' && isNaN(Number(p)))) return null;
  const n = Number(p);
  const asPct = n <= 1 ? n * 100 : n;
  const r = Math.round(asPct * 10) / 10;
  const s = r % 1 === 0 ? String(Math.round(r)) : r.toFixed(1);
  return s + '%';
};

// Helper: Get guest display name (robust field resolution, trimmed, fallback to "Guest")
// Defined outside component to avoid initialization issues
const getGuestDisplayName = (booking) => {
  if (!booking) return 'Guest';
  
  // Try multiple field names in priority order
  let name = booking.guest_name || 
             booking.guestName || 
             booking.guest_full_name ||
             booking.guest ||
             booking.guestLabel ||
             booking.guest_label;
  
  // If we have a title/display_label that matches "Name • X guests" pattern, parse it
  if (!name) {
    const title = booking.title || booking.display_label || booking.calendar_label;
    if (title && typeof title === 'string') {
      // Try to parse "Name • X guests" format
      const match = title.match(/^([^•]+?)(?:\s*•.*)?$/);
      if (match && match[1]) {
        name = match[1].trim();
      }
    }
  }
  
  // Trim and return, fallback to "Guest"
  return (name && typeof name === 'string' ? name.trim() : '') || 'Guest';
};

// Helper: Get guest count (robust field resolution, integer or null)
// Defined outside component to avoid initialization issues
const getGuestCount = (booking) => {
  if (!booking) return null;
  
  // Try multiple field names in priority order
  let count = booking.guest_count || 
              booking.guestCount || 
              booking.guests || 
              booking.number_of_guests;
  
  // If adults/children exist, sum them
  if (!count && (booking.adults || booking.children)) {
    const adults = typeof booking.adults === 'number' ? booking.adults : 0;
    const children = typeof booking.children === 'number' ? booking.children : 0;
    count = adults + children;
  }
  
  // If we have a title/display_label that matches "Name • X guests" pattern, parse it
  if (!count) {
    const title = booking.title || booking.display_label || booking.calendar_label;
    if (title && typeof title === 'string') {
      // Try to parse "Name • X guests" format
      const match = title.match(/•\s*(\d+)\s*guests?/i);
      if (match && match[1]) {
        count = parseInt(match[1], 10);
      }
    }
  }
  
  // Return integer if valid, else null
  if (typeof count === 'number' && count > 0) return count;
  return null;
};

// Helper: Get subtitle parts array (filters nulls)
// Defined outside component to avoid initialization issues
const getGuestSubtitleParts = (booking) => {
  const parts = [];
  
  // Guest count
  const guestCount = getGuestCount(booking);
  if (guestCount) {
    parts.push(`${guestCount} ${guestCount === 1 ? 'guest' : 'guests'}`);
  }
  
  // Date range - use ET formatting
  const checkin = booking.check_in_date || booking.checkin_date;
  const checkout = booking.check_out_date || booking.checkout_date;
  if (checkin && checkout) {
    // Use formatDateRangeET for consistent ET formatting
    const dateRange = formatDateRangeET(checkin, checkout, { short: true });
    if (dateRange !== 'N/A') {
      parts.push(dateRange);
    }
  }
  
  // Nights
  const nights = booking.nights || booking.nights_count || 0;
  if (nights > 0) {
    parts.push(`${nights} ${nights === 1 ? 'night' : 'nights'}`);
  }
  
  // Channel
  const channel = booking.channel_code || booking.channel;
  if (channel && channel !== 'Direct') {
    parts.push(channel);
  }
  
  return parts;
};

// Helper: Get initials from guest name
// Defined outside component to avoid initialization issues
const getInitials = (name) => {
  if (!name || name === 'Guest') return 'G';
  const trimmed = name.trim();
  const words = trimmed.split(/\s+/).filter(w => w.length > 0);
  if (words.length === 0) return 'G';
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  // First letter of first word + first letter of last word
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
};

function isPendingOwnerStayBooking(booking) {
  if (!booking) return false;
  const reservationId = String(booking.reservation_id || booking.id || '');
  return booking.pending_sync === true || reservationId.startsWith('pending-owner-stay-');
}

function isOwnerStayBooking(booking) {
  if (!booking) return false;
  return checkOwnerStay(booking.canonical_financial_summary || null, booking, null);
}

function getPendingOwnerStayDisplayId(booking) {
  if (!booking) return null;
  if (booking.hostaway_reservation_id) return booking.hostaway_reservation_id;
  const reservationId = String(booking.reservation_id || booking.id || '');
  if (reservationId.startsWith('pending-owner-stay-')) {
    return reservationId.replace('pending-owner-stay-', '');
  }
  return reservationId || null;
}

function getManageableOwnerStayReservationId(booking) {
  const rawId = booking?.hostaway_reservation_id || booking?.reservation_id || booking?.id;
  const id = Number(rawId);
  return Number.isFinite(id) && id > 0 ? id : null;
}

export default function BookingDetailModal({
  isOpen,
  booking,
  onClose,
  comments = [],
  onAddComment,
  onEditOwnerStay,
  onCancelOwnerStay,
}) {
  const { theme } = useTheme();
  const { isAdminUser } = useSession();
  const [isMobile, setIsMobile] = useState(false);
  const [isFinancialModalOpen, setIsFinancialModalOpen] = useState(false);
  const [localComments, setLocalComments] = useState(comments);
  const [topPanelOpen, setTopPanelOpen] = useState(null); // 'reviews' | 'notes' | 'files' | null
  const [ownerStayActionError, setOwnerStayActionError] = useState(null);
  const [ownerStayActionSubmitting, setOwnerStayActionSubmitting] = useState(null);
  const { tooltipState, showTooltip, hideTooltip, cancelHideTooltip } = useTooltipPortal();
  const ownerStayBooking = isOwnerStayBooking(booking);
  const pendingOwnerStay = ownerStayBooking && isPendingOwnerStayBooking(booking);
  const manageableOwnerStayReservationId = ownerStayBooking ? getManageableOwnerStayReservationId(booking) : null;
  const shouldFetchReservationData = !ownerStayBooking;
  const reservationIdForFetch = shouldFetchReservationData ? (booking?.reservation_id || booking?.id) : null;
  const { detailLoading, detailError, detailData, refreshDetailData } = useBookingDetailData({
    isOpen: isOpen && shouldFetchReservationData,
    reservationId: reservationIdForFetch,
    getReservationDetailFn: getReservationDetail,
  });
  const { notesLoading, notesError, notes } = useBookingNotes({
    isOpen: isOpen && shouldFetchReservationData,
    reservationId: reservationIdForFetch,
    getReservationNotesFn: getReservationNotes,
  });
  const { reviewsLoading, reviewsError, reviews } = useBookingReviews({
    isOpen: isOpen && shouldFetchReservationData,
    reservationId: reservationIdForFetch,
    getReservationReviewsFn: getReservationReviews,
  });
  const { filesLoading, filesError, files, hasFiles, refreshFiles } = useBookingFiles({
    isOpen: isOpen && shouldFetchReservationData,
    reservationId: reservationIdForFetch,
    listingId: booking?.listing_id ?? null,
    topPanelOpen: shouldFetchReservationData ? topPanelOpen : null,
    getReservationHasFilesFn: getReservationHasFiles,
    getReservationLinksFn: getReservationLinks,
  });
  const { linksLoading, linksError, links } = useBookingLinks({
    isOpen: isOpen && shouldFetchReservationData,
    reservationId: reservationIdForFetch,
    listingId: booking?.listing_id ?? null,
    linksEnabled: OWNER_FEATURES.links,
    getReservationLinksFn: getReservationLinks,
  });

  useEffect(() => {
    if (!isOpen) {
      setOwnerStayActionError(null);
      setOwnerStayActionSubmitting(null);
      return;
    }
    setOwnerStayActionError(null);
  }, [isOpen, booking?.reservation_id, booking?.id]);

  // Gate Admin Parity by admin privileges (isAdminUser), not just effectiveMode
  // isAdminUser is true when user has admin privileges (isSuperAdmin, capabilities, role)
  // effectiveMode can be 'owner_view' when admin is viewing as owner, but Admin Parity should still show
  const canSeeAdminParity = isAdminUser;

  // DEBUG: Log booking object shape when modal opens (only with ?debug=1)
  useEffect(() => {
    if (isOpen && booking) {
      const urlParams = new URLSearchParams(window.location.search);
      const debug = urlParams.get('debug') === '1';
      
      if (debug) {
        console.log('[BookingDetailModal] booking keys:', Object.keys(booking || {}));
        console.log('[BookingDetailModal] booking sample:', {
          guest_name: booking.guest_name,
          guestName: booking.guestName,
          guest_count: booking.guest_count,
          guests: booking.guests,
          reservation_id: booking.reservation_id,
          channel_code: booking.channel_code,
          channel: booking.channel,
        });
        
        // Log financial fields
        const financialFields = [
          'airbnb_expected_payout_amount',
          'airbnb_total_paid_amount',
          'channel_commission_amount',
          'host_payout',
          'channel_fee',
          'payout_amount',
          'payment_service_processing_fee',
          'bookingcom_payment_processing_fee',
          'booking_fee',
          'early_checkin_fee',
          'monthly_discount',
          'coupon_discount',
          'discount_amount',
          'airbnb_transient_occupancy_tax_paid_amount',
        ];
        const financials = {};
        financialFields.forEach(field => {
          if (booking[field] !== undefined && booking[field] !== null) {
            financials[field] = booking[field];
          }
        });
        console.log('[BookingDetailModal] financial fields:', financials);
      }
    }
  }, [isOpen, booking]);

  // Body scroll lock when modal is open
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      const originalPaddingRight = document.body.style.paddingRight;
      // Calculate scrollbar width to prevent layout shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.paddingRight = originalPaddingRight;
      };
    }
  }, [isOpen]);

  useEffect(() => {
    const checkMobile = () => window.innerWidth < 768;
    setIsMobile(checkMobile());
    window.addEventListener('resize', () => setIsMobile(checkMobile()));
    return () => window.removeEventListener('resize', () => setIsMobile(checkMobile()));
  }, []);
  
  // Attach escape key listener
  useEffect(() => {
    if (isOpen) {
      const escapeHandler = (e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };
      document.addEventListener('keydown', escapeHandler);
      return () => {
        document.removeEventListener('keydown', escapeHandler);
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen || !booking) {
    return null;
  }

  // All functions and values defined AFTER early return (when booking is guaranteed to exist)
  // Debug logging for ET timezone (gated by ?perf_debug=1)
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const perfDebug = urlParams?.get('perf_debug') === '1';
  
  // STOPPOINT 1: Debug logging to capture ALL date fields and identify mismatch root cause
  if (perfDebug) {
    // Collect all date field variations from booking
    const bookingDateFields = {
      check_in_date: booking?.check_in_date,
      check_out_date: booking?.check_out_date,
      checkin_date: booking?.checkin_date,
      checkout_date: booking?.checkout_date,
      checkIn: booking?.checkIn,
      checkOut: booking?.checkOut,
      checkInDate: booking?.checkInDate,
      checkOutDate: booking?.checkOutDate,
      arrivalDate: booking?.arrivalDate,
      departureDate: booking?.departureDate,
      start_date: booking?.start_date,
      end_date: booking?.end_date,
      booked_at: booking?.booked_at,
      booking_date: booking?.booking_date,
      created_at: booking?.created_at,
      updated_at: booking?.updated_at,
    };
    
    // Collect all date field variations from detailData
    const detailDateFields = detailData ? {
      check_in_date: detailData?.check_in_date,
      check_out_date: detailData?.check_out_date,
      checkin_date: detailData?.checkin_date,
      checkout_date: detailData?.checkout_date,
      checkIn: detailData?.checkIn,
      checkOut: detailData?.checkOut,
      arrivalDate: detailData?.arrivalDate,
      departureDate: detailData?.departureDate,
      start_date: detailData?.start_date,
      end_date: detailData?.end_date,
      booked_at: detailData?.booked_at,
      booking_date: detailData?.booking_date,
      created_at: detailData?.created_at,
      updated_at: detailData?.updated_at,
    } : null;
    
    // Test formatting of each candidate field
    const testFormatting = (fieldValue, fieldName) => {
      if (!fieldValue) return null;
      try {
        if (typeof fieldValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(fieldValue)) {
          // Date-only format
          return {
            raw: fieldValue,
            formatted: formatDateOnlyET(fieldValue, { long: true }),
            type: 'date-only',
          };
        } else {
          // Timestamp format
          return {
            raw: fieldValue,
            formatted: formatDateTimeET(fieldValue, { dateOnly: true, long: true }),
            type: 'timestamp',
          };
        }
      } catch (e) {
        return { raw: fieldValue, error: e.message };
      }
    };
    
    const bookingFormatted = {};
    for (const [key, value] of Object.entries(bookingDateFields)) {
      if (value) bookingFormatted[key] = testFormatting(value, key);
    }
    
    const detailFormatted = {};
    if (detailDateFields) {
      for (const [key, value] of Object.entries(detailDateFields)) {
        if (value) detailFormatted[key] = testFormatting(value, key);
      }
    }
    
    console.log('[BookingDetailModal] ALL DATE FIELDS DEBUG:', {
      reservation_id: booking?.reservation_id,
      bookingDateFields,
      detailDateFields,
      bookingFormatted,
      detailFormatted,
      // All booking keys for reference
      bookingKeys: Object.keys(booking || {}).filter(k => k.includes('check') || k.includes('date') || k.includes('night') || k.includes('at')),
      // Detail data keys (if available)
      detailDataKeys: detailData ? Object.keys(detailData).filter(k => k.includes('check') || k.includes('date') || k.includes('night') || k.includes('at')) : null,
    });
  }
  
  // Determine canonical date fields (prefer detailData if available, fallback to booking)
  // Include all discovered aliases to ensure we catch the correct field
  const canonicalCheckIn = 
    detailData?.check_in_date || detailData?.checkin_date || 
    detailData?.arrivalDate || detailData?.start_date ||
    booking?.check_in_date || booking?.checkin_date ||
    booking?.arrivalDate || booking?.start_date;
  
  const canonicalCheckOut = 
    detailData?.check_out_date || detailData?.checkout_date ||
    detailData?.departureDate || detailData?.end_date ||
    booking?.check_out_date || booking?.checkout_date ||
    booking?.departureDate || booking?.end_date;
  
  // Determine canonical booking_date (prefer detailData if available, fallback to booking)
  // Also check for booked_at, created_at, inserted_on as fallbacks
  const canonicalBookingDate = detailData?.booking_date || detailData?.booked_at || detailData?.created_at || detailData?.inserted_on ||
                               booking?.booking_date || booking?.booked_at || booking?.created_at || booking?.inserted_on;
  
  // Determine canonical latest extracted timestamp (from finance_fields_meta or detailData)
  const financeMeta = detailData?.finance_fields_meta;
  const canonicalLatestExtractedAt = financeMeta?.latestExtractedAt || detailData?.latest_extracted_at || detailData?.extracted_at ||
                                     booking?.latest_extracted_at || booking?.extracted_at;
  
  // Debug: Log which fields are used for header vs Stay Details
  if (perfDebug) {
    console.log('[BookingDetailModal] CANONICAL DATE FIELDS:', {
      canonicalCheckIn,
      canonicalCheckOut,
      canonicalBookingDate,
      canonicalLatestExtractedAt,
      headerSource: 'getGuestSubtitleParts -> formatDateRangeET',
      stayDetailsSource: 'formatDate(canonicalCheckIn/CheckOut)',
      bookingDateSource: 'formatDate(canonicalBookingDate)',
      latestSource: 'formatDateTimeET(canonicalLatestExtractedAt)',
      note: 'All dates should use canonical fields',
    });
  }
  
  const formatDateRange = () => {
    // Use canonical fields for consistent date range display
    const parts = [];
    
    // Guest count
    const guestCount = getGuestCount(booking);
    if (guestCount) {
      parts.push(`${guestCount} ${guestCount === 1 ? 'guest' : 'guests'}`);
    }
    
    // Date range - use canonical fields with ET formatting
    if (canonicalCheckIn && canonicalCheckOut) {
      const dateRange = formatDateRangeET(canonicalCheckIn, canonicalCheckOut, { short: true });
      if (dateRange !== 'N/A') {
        parts.push(dateRange);
      }
    }
    
    // Nights
    const nights = booking?.nights || booking?.nights_count || 0;
    if (nights > 0) {
      parts.push(`${nights} ${nights === 1 ? 'night' : 'nights'}`);
    }
    
    // Remove channel from parts (will be added separately with dot)
    const channel = booking?.channel_code || booking?.channel;
    const partsWithoutChannel = parts.filter(p => !channel || p !== channel);
    
    if (perfDebug) {
      console.log('[BookingDetailModal] formatDateRange result:', {
        dateRange: partsWithoutChannel.join(' • '),
        canonicalCheckIn,
        canonicalCheckOut,
      });
    }
    
    return partsWithoutChannel.length > 0 ? partsWithoutChannel.join(' • ') : 'Dates not available';
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // MASTER CHANNEL COLOR/LABEL (canonical-first approach)
  // INTENT: Canonical/persisted selection — detailData canonical preferred (detail endpoint); booking used when modal opens before detail fetch. Persisted summary (financials_persisted_summary) is source of truth for financial numbers in folio; no fallback to canonical for amounts.
  const canonical = resolveCanonical(detailData, booking);
  const channelCode = booking?.channel_code || booking?.channel;
  
  // Use shared strict resolver (single source of truth) - do not use backend master_channel_color
  // Resolve master channel and use CSS variable via shared resolver
  const masterChannel = resolveMasterChannel(channelCode, null, booking?.status || null, booking?.clc_channels || null, canonical);
  const sourceColor = getChannelColorVar(masterChannel.master_channel_key);
  
  // Compute RGBA accent color from the hex value (use getChannelColorHex for RGBA calculation)
  const sourceColorHex = getChannelColorHex(masterChannel.master_channel_key);
  const accentColor = sourceColorHex ? (() => {
    const hex = sourceColorHex;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, 0.10)`;
  })() : null;
  
  // Use canonical master channel label if available (forced lease wins)
  // If canonical exists, use it (even if it's from booking, not detailData)
  // Only fallback to sourceColors if canonical is completely missing
  const sourceChannelDisplay = canonical?.master_channel_label || 
                               (channelCode ? getSourceChannelDisplay(channelCode, channelCode) : null);
  
  // Get reservation ID for header
  const reservationId = pendingOwnerStay
    ? getPendingOwnerStayDisplayId(booking)
    : (booking?.id || booking?.reservation_id || null);
  
  // Get guest count for header
  const guestCount = getGuestCount(booking);
  
  // Get nights for header
  const nights = booking?.nights || booking?.nights_count || null;
  
  // Reservations date contract: Florida/ET calendar dates. Normalize to YYYY-MM-DD so table and modal match (no off-by-one from ISO timestamps).
  const checkinDate = canonicalCheckIn ? formatDateOnlyET(normalizeToYmd(canonicalCheckIn) || canonicalCheckIn, { short: true }) : null;
  const checkoutDate = canonicalCheckOut ? formatDateOnlyET(normalizeToYmd(canonicalCheckOut) || canonicalCheckOut, { short: true }) : null;
  
  // Channel visual indicator (professional - colored dot only, no letter glyphs)
  // Returns null - we'll use just the colored dot + channel name

  // Prepare booking for financial modal (used in embedded mobile view and overlay)
  const persistedSummaryForModal = detailData?.financials_persisted_summary
    ?? detailData?.persisted_summary
    ?? detailData?.persistedSummary
    ?? detailData?.reservation?.financials_persisted_summary
    ?? null;
  const bookingForFinancialModal = booking ? {
    ...booking,
    canonical_financial_summary: resolveCanonical(detailData, booking),
    financials_persisted_summary: persistedSummaryForModal,
    reservation_id: booking.reservation_id || booking.id,
  } : null;

  const handleEditOwnerStay = () => {
    if (!manageableOwnerStayReservationId || ownerStayActionSubmitting) return;
    setOwnerStayActionError(null);
    onEditOwnerStay?.(booking);
  };

  const handleCancelOwnerStay = async () => {
    if (!manageableOwnerStayReservationId || ownerStayActionSubmitting || !onCancelOwnerStay) return;
    const confirmed = window.confirm('Cancel this owner stay?');
    if (!confirmed) return;

    setOwnerStayActionError(null);
    setOwnerStayActionSubmitting('cancel');
    try {
      await onCancelOwnerStay(booking);
      setOwnerStayActionSubmitting(null);
      onClose();
    } catch (error) {
      setOwnerStayActionError(error?.message || 'Owner stay could not be cancelled.');
      setOwnerStayActionSubmitting(null);
    }
  };

  const ownerStayActions = ownerStayBooking && manageableOwnerStayReservationId ? (
    <div style={styles.ownerStayActionWrap}>
      {ownerStayActionError && (
        <div style={styles.ownerStayActionError} role="alert">
          {ownerStayActionError}
        </div>
      )}
      <div style={styles.ownerStayActionRow}>
        <button
          type="button"
          style={styles.ownerStaySecondaryAction}
          onClick={handleEditOwnerStay}
          disabled={!!ownerStayActionSubmitting || !onEditOwnerStay}
        >
          <Edit3 size={15} strokeWidth={2.2} />
          <span>Edit Stay</span>
        </button>
        <button
          type="button"
          style={styles.ownerStayDangerAction}
          onClick={handleCancelOwnerStay}
          disabled={!!ownerStayActionSubmitting || !onCancelOwnerStay}
        >
          <Trash2 size={15} strokeWidth={2.2} />
          <span>{ownerStayActionSubmitting === 'cancel' ? 'Cancelling...' : 'Cancel Stay'}</span>
        </button>
      </div>
    </div>
  ) : null;

  const ModalContent = (
    <>
      <BookingDetailHeader
        theme={theme}
        onClose={onClose}
        styles={styles}
        guestDisplayName={getGuestDisplayName(booking)}
        initials={getInitials(getGuestDisplayName(booking))}
        reservationId={reservationId}
        channelCode={channelCode}
        sourceColor={sourceColor}
        sourceChannelDisplay={sourceChannelDisplay}
        checkinDate={checkinDate}
        checkoutDate={checkoutDate}
        nights={nights}
        guestCount={guestCount}
        showDoneButton={isMobile}
      />

      <div style={{
        ...styles.content,
        backgroundColor: theme === 'dark' 
          ? 'rgba(30, 30, 30, 0.95)' 
          : 'rgba(250, 248, 245, 0.95)', // Modal background = folio surface
      }}>
        {/* FULL MODAL FOLIO VIEWER - Financial folio owns entire modal canvas */}
        <div style={styles.folioViewerContainer}>
          {/* Mobile: combined view — financials (Summary | Breakdown) inline; no middle step */}
          {!ownerStayBooking && isMobile && bookingForFinancialModal && (
            <FinancialDetailsModal
              embedInSheet
              booking={bookingForFinancialModal}
              isMobile={true}
              onRetryFetch={() => refreshDetailData()}
            />
          )}
          {/* Financial Folio Sheet - Primary Content (desktop + mobile icons/panels) */}
          {(() => {
                  // CANONICAL FINANCIAL SUMMARY (owner-facing contract)
                  // INTENT: Same canonical selection as header; persisted summary drives all financial display — no fallback to canonical for numbers.
                  const canonical = resolveCanonical(detailData, booking);
                  const isForcedLease = checkForcedLease(canonical);
                  const isOwnerStay = ownerStayBooking || checkOwnerStay(canonical, booking, detailData);

                  if (isOwnerStay) {
                    return (
                      <div style={{
                        ...styles.folioSheetContainer,
                        backgroundColor: theme === 'dark'
                          ? 'rgba(30, 30, 30, 0.95)'
                          : 'rgba(250, 248, 245, 0.95)',
                        border: 'none',
                        boxShadow: 'none',
                      }}>
                        <OwnerStaySection theme={theme} variant={isMobile ? 'mobile' : 'desktop'}>
                          {ownerStayActions}
                        </OwnerStaySection>
                      </div>
                    );
                  }
                  
                  // Helper: Format value for display
                  const formatFinanceValue = (value) => {
                    if (value === null || value === undefined) {
                      return '—';
                    }
                    if (typeof value === 'number') {
                      return formatCurrency(value);
                    }
                    if (typeof value === 'string') {
                      const parsed = parseFloat(value);
                      if (!isNaN(parsed)) {
                        return formatCurrency(parsed);
                      }
                    }
                    return String(value);
                  };
                  
                  // Persisted-only modal cutover: Use financials_persisted_summary as source of truth.
                  // Robust summary selection (API may expose at top level, under reservation, or as persisted_summary).
                  const summary = detailData?.financials_persisted_summary
                    ?? detailData?.persisted_summary
                    ?? detailData?.persistedSummary
                    ?? detailData?.reservation?.financials_persisted_summary
                    ?? null;
                  const persistedAvailable = detailData?.persisted_available ?? !!summary;
                  const persistedSummary = summary;

                  // If persisted not available, show empty state (no fallback to canonical/MV)
                  if (!persistedAvailable || !persistedSummary) {
                    // Empty state will be handled in render - return early for financial sections
                    // Continue with rest of modal (reservation details, etc.)
                  }

                  // Channel fee line items: use normalized list when present; else derive for backward compat (old API)
                  const channelFeeLineItems = (() => {
                    if (persistedSummary?.channel_fee_line_items?.length) return persistedSummary.channel_fee_line_items;
                    const items = [];
                    const ownerCf = persistedSummary?.owner_payout?.owner_channel_fees;
                    if (ownerCf != null && parseFloat(ownerCf) !== 0) items.push({ amount: Math.round(parseFloat(ownerCf) * 100) / 100, borneBy: 'owner', labelKey: 'channel_fees' });
                    const guestCf = persistedSummary?.guest_paid?.guest_channel_fee;
                    if (guestCf != null && parseFloat(guestCf) !== 0) items.push({ amount: Math.round(parseFloat(guestCf) * 100) / 100, borneBy: 'guest', labelKey: 'channel_fees' });
                    return items;
                  })();

                  // Map persisted summary to UI structure
                  // Weekly discount: support all API key variants; normalize once to number or null
                  const rawWeekly = summary?.guest_paid?.guest_weekly_discount
                    ?? summary?.guest_paid?.guestWeeklyDiscount
                    ?? summary?.guest_paid?.weekly_discount
                    ?? summary?.guest_paid?.weeklyDiscount
                    ?? null;
                  const weeklyNum = rawWeekly == null ? null : Number(rawWeekly);
                  const weeklyDiscount = Number.isFinite(weeklyNum) ? weeklyNum : null;
                  const guestPaid = persistedSummary?.guest_paid ? {
                    baseRate: persistedSummary.guest_paid.guest_base_rate,
                    cleaningFee: persistedSummary.guest_paid.guest_cleaning_fee,
                    touristTax: persistedSummary.guest_paid.guest_tourist_tax,
                    discountLineItems: persistedSummary.guest_paid.discount_line_items,
                    discountsTotal: persistedSummary.guest_paid.guest_discounts_total,
                    couponDiscount: persistedSummary.guest_paid.guest_coupon_discount ?? null,
                    weeklyDiscount,
                    totalCollected: persistedSummary.guest_paid.guest_total_paid,
                    guestServiceFee: persistedSummary.guest_paid.guest_service_fee || null,
                    guestChannelFee: persistedSummary.guest_paid.guest_channel_fee || null,
                    stateTaxAmount: persistedSummary.guest_paid.state_tax_amount ?? null,
                    countyTaxAmount: persistedSummary.guest_paid.county_tax_amount ?? null,
                    addOnFees: persistedSummary.guest_paid.add_on_fees ?? [],
                    totalReservationAmount: persistedSummary.guest_paid.total_reservation_amount ?? null,
                    damageDepositAmount: persistedSummary.guest_paid.damage_deposit_amount ?? null,
                  } : null;

                  // Persisted-only modal cutover: Use ONLY persisted data for financial display
                  // No fallback to canonical for financial numbers
                  // Crissy: CLC/bookingengine use resolved payout (estimated or authoritative); never "Unknown" when estimate exists
                  const isCLC = persistedSummary?.channel?.master_channel_key === 'clc';
                  const payoutSt = persistedSummary?.owner_payout?.owner_payout_status;
                  const resolvedPayoutDisplay = (isCLC && (payoutSt === 'estimated' || payoutSt === 'deposited')) ? payoutSt : null;
                  const hostPayout = persistedSummary?.owner_payout ? {
                    baseRate: persistedSummary.owner_payout.owner_base_rate,
                    discountLineItems: persistedSummary.guest_paid?.discount_line_items,
                    discountsTotal: persistedSummary.guest_paid?.guest_discounts_total,
                    couponDiscount: persistedSummary.guest_paid?.guest_coupon_discount ?? null,
                    weeklyDiscount,
                    cleaningFee: persistedSummary.owner_payout.owner_cleaning_fee,
                    channelFees: persistedSummary.owner_payout.owner_channel_fees,
                    adjustments: persistedSummary.owner_payout.owner_adjustments,
                    ownerPayoutTotal: persistedSummary.owner_payout.owner_payout_total,
                    ownerPayoutStatus: persistedSummary.owner_payout.owner_payout_status,
                    ownerPayoutBase: persistedSummary.owner_payout.owner_payout_base,
                    estimatedPayout: persistedSummary.owner_payout.owner_payout_total,
                    adjustmentsTotal: persistedSummary.owner_payout.owner_adjustments || null,
                    adjustmentsBreakdown: persistedSummary.owner_payout.owner_adjustments_breakdown || [], // Array of adjustment objects
                    payoutStatus: persistedSummary.owner_payout.owner_payout_status,
                    estimatedPayoutDate: null,
                    payoutDisplayMode: resolvedPayoutDisplay === 'estimated' ? 'estimated' : 'final',
                    copyMode: resolvedPayoutDisplay === 'estimated' ? 'provisional' : 'final',
                    isProvisionalCopy: resolvedPayoutDisplay === 'estimated',
                    payoutLabel: resolvedPayoutDisplay === 'estimated' ? 'Estimated' : (resolvedPayoutDisplay === 'deposited' ? 'Final' : null),
                    // VRBO computed fields - not available in persisted
                    vrboHostPayoutSoFar: null,
                    vrboEstimatedDeposit: null,
                    // Lease-specific fields - not available in persisted
                    ownerTouristTax: null,
                    ownerDamageDeposit: null,
                  } : null;

                  // Loading state
                  if (detailLoading) {
                    return (
                      <div style={styles.emptyStateMessage}>
                        <p style={styles.emptyStateText}>Loading finance data...</p>
                      </div>
                    );
                  }

                  // Error state
                  if (detailError) {
                    return (
                      <div style={styles.emptyStateMessage}>
                        <p style={styles.emptyStateText}>Error loading finance data: {detailError}</p>
                      </div>
                    );
                  }

                  // Persisted-only modal cutover: Check if persisted financials are available
                  // If not available, show clear empty state (no fallback to canonical/MV)
                  if (!persistedAvailable || !persistedSummary) {
                    const reason = detailData?.persisted_unavailable_reason || 'No persisted financial record found';
                    return (
                      <div style={styles.emptyStateMessage}>
                        <p style={styles.emptyStateText}>
                          Persisted financials not available yet for this reservation.
                        </p>
                        <p style={{ ...styles.emptyStateText, fontSize: '12px', marginTop: '8px', opacity: 0.7 }}>
                          Reservation ID: {reservationId}
                        </p>
                        <p style={{ ...styles.emptyStateText, fontSize: '12px', marginTop: '4px', opacity: 0.7 }}>
                          {reason}
                        </p>
                        <p style={{ ...styles.emptyStateText, fontSize: '12px', marginTop: '8px', opacity: 0.7 }}>
                          If this persists after the next sync, contact admin.
                        </p>
                      </div>
                    );
                  }

                  // Render financial summary - Luxury Hotel Folio Sheet (Option B: Modern Luxury Folio)
                  // Reviews / Notes content detection (scaffold for future data integration)
                  // TODO: Wire to real reviews/notes data when available
                  // For now, derive defensively from reservation/detail payload if anything exists
                  // Debug flags for testing: ?debug_reviews=1 and ?debug_notes=1
                  const debugReviews = urlParams?.get('debug_reviews') === '1';
                  const debugNotes = urlParams?.get('debug_notes') === '1';
                  
                  // Derive hasReviews: check if reviews array has items (defensive, default to false)
                  // Note: reviews array is populated when panel opens, but indicator should show based on fetched state
                  // We check the state variable which persists even when panel closes
                  const hasReviews = debugReviews || (reviews && Array.isArray(reviews) && reviews.length > 0);
                  
                  // Derive hasNotes: check if notes array has items (defensive, default to false)
                  // Note: notes array is populated when panel opens, but indicator should show based on fetched state
                  // We check the state variable which persists even when panel closes
                  const hasNotes = debugNotes || (notes && Array.isArray(notes) && notes.length > 0);

                  const handleOwnerPayoutTooltip = (e) => {
                    const copyMode = hostPayout?.copyMode || hostPayout?.payoutDisplayMode || 'provisional';
                    const isFinalCopy = copyMode === 'final';
                    const tooltipCanonical = resolveCanonical(detailData, booking);
                    const isLegacyVRBOiCalVal = persistedSummary?.tourist_tax?.is_legacy_vrbo_ical ?? checkLegacyVRBOiCal(tooltipCanonical);
                    const isIntegratedVrboNonIcal = tooltipCanonical?.master_channel_key === 'vrbo' && !isLegacyVRBOiCalVal;
                    const daysUntilCheckIn = getDaysUntilCheckIn(canonicalCheckIn);
                    const tooltipText = getOwnerPayoutTooltipText({
                      isFinalCopy,
                      isLegacyVRBOiCal: isLegacyVRBOiCalVal,
                      isVRBONonICalComputed: checkVRBONonICalComputed(tooltipCanonical),
                      isVRBOStripe: checkVRBOStripe(tooltipCanonical),
                      isIntegratedVrboNonIcal,
                      daysUntilCheckIn,
                      isCLCLease: checkCLCLease(tooltipCanonical),
                      isBookingEngine: checkBookingEngine(tooltipCanonical),
                      isDirect: checkDirect(tooltipCanonical),
                      isHomeAway: checkHomeAway(tooltipCanonical),
                    });
                    showTooltip(e, tooltipText);
                  };

                  // Get reservation context for header
                  const reservationNumber = booking?.id || booking?.reservation_id || '—';
                  const nights = booking?.nights || null;
                  const checkinDate = booking?.checkin_date ? formatDateOnlyET(booking.checkin_date) : null;
                  const checkoutDate = booking?.checkout_date ? formatDateOnlyET(booking.checkout_date) : null;
                  const dateRange = checkinDate && checkoutDate ? `${checkinDate} - ${checkoutDate}` : null;

                  return (
                    <>
                      {/* FOLIO SURFACE - Modal background = folio surface (no "sheet inside white") */}
                      <div style={{
                        ...styles.folioSheetContainer,
                        backgroundColor: theme === 'dark' 
                          ? 'rgba(30, 30, 30, 0.95)' 
                          : 'rgba(250, 248, 245, 0.95)',
                        border: 'none', // No border - modal background is folio surface
                        boxShadow: 'none', // No shadow - modal background is folio surface
                      }}>
                        {/* TOP ACTIONS: Reviews, Notes, and Files icons (always visible) */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '4px 24px 8px 24px', // Reduced vertical padding: 4px top, 8px bottom (total ~12px)
                        }}>
                          {/* Reviews Icon Button */}
                          <button
                            onClick={() => {
                              setTopPanelOpen(topPanelOpen === 'reviews' ? null : 'reviews');
                            }}
                            style={{
                              width: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              borderRadius: '6px',
                              transition: 'background-color 0.2s ease',
                              padding: '6px',
                              position: 'relative', // For dot indicator positioning
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = theme === 'dark' 
                                ? 'rgba(255, 255, 255, 0.1)' 
                                : 'rgba(0, 0, 0, 0.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                            title={hasReviews ? "Reviews available" : "Reviews (coming soon)"}
                            aria-label={hasReviews ? "Reviews available" : "Reviews (coming soon)"}
                          >
                            <svg 
                              width="20" 
                              height="20" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              xmlns="http://www.w3.org/2000/svg"
                              style={{ 
                                stroke: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                                strokeWidth: '1.5',
                                strokeLinecap: 'round',
                                strokeLinejoin: 'round',
                                opacity: topPanelOpen === 'reviews' ? 1 : 0.7,
                              }}
                            >
                              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                              <path d="M8 9h8M8 13h6" />
                            </svg>
                            {/* Content indicator dot (only when hasReviews) - more noticeable */}
                            {hasReviews && (
                              <div style={{
                                position: 'absolute',
                                top: '4px',
                                right: '4px',
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                backgroundColor: theme === 'dark' ? '#60A5FA' : '#3B82F6', // Higher contrast blue
                                border: `2px solid ${theme === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(250, 248, 245, 0.95)'}`, // Match modal background
                                boxSizing: 'border-box',
                                pointerEvents: 'none', // Don't block clicks
                                boxShadow: theme === 'dark' 
                                  ? '0 0 0 1px rgba(96, 165, 250, 0.3)' 
                                  : '0 0 0 1px rgba(59, 130, 246, 0.3)', // Subtle glow for visibility
                              }} />
                            )}
                          </button>
                          
                          {/* Notes Icon Button */}
                          <button
                            onClick={() => {
                              setTopPanelOpen(topPanelOpen === 'notes' ? null : 'notes');
                            }}
                            style={{
                              width: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              borderRadius: '6px',
                              transition: 'background-color 0.2s ease',
                              padding: '6px',
                              position: 'relative', // For dot indicator positioning
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = theme === 'dark' 
                                ? 'rgba(255, 255, 255, 0.1)' 
                                : 'rgba(0, 0, 0, 0.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                            title={hasNotes ? "Notes available" : "Notes (coming soon)"}
                            aria-label={hasNotes ? "Notes available" : "Notes (coming soon)"}
                          >
                            <svg 
                              width="20" 
                              height="20" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              xmlns="http://www.w3.org/2000/svg"
                              style={{ 
                                stroke: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                                strokeWidth: '1.5',
                                strokeLinecap: 'round',
                                strokeLinejoin: 'round',
                                opacity: topPanelOpen === 'notes' ? 1 : 0.7,
                              }}
                            >
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                              <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
                            </svg>
                            {/* Content indicator dot (only when hasNotes) - more noticeable */}
                            {hasNotes && (
                              <div style={{
                                position: 'absolute',
                                top: '4px',
                                right: '4px',
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                backgroundColor: theme === 'dark' ? '#60A5FA' : '#3B82F6', // Higher contrast blue
                                border: `2px solid ${theme === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(250, 248, 245, 0.95)'}`, // Match modal background
                                boxSizing: 'border-box',
                                pointerEvents: 'none', // Don't block clicks
                                boxShadow: theme === 'dark' 
                                  ? '0 0 0 1px rgba(96, 165, 250, 0.3)' 
                                  : '0 0 0 1px rgba(59, 130, 246, 0.3)', // Subtle glow for visibility
                              }} />
                            )}
                          </button>

                          {/* Files Icon Button (always visible; blue dot when reservation-linked files exist) */}
                          <button
                            type="button"
                            data-test="reservation-files-toggle"
                            onClick={() => {
                              setTopPanelOpen(topPanelOpen === 'files' ? null : 'files');
                            }}
                            style={{
                              width: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              borderRadius: '6px',
                              transition: 'background-color 0.2s ease',
                              padding: '6px',
                              position: 'relative',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = theme === 'dark'
                                ? 'rgba(255, 255, 255, 0.1)'
                                : 'rgba(0, 0, 0, 0.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                            title={hasFiles ? 'Linked files available' : 'Linked files for this reservation'}
                            aria-label={hasFiles ? 'Linked files available' : 'Linked files for this reservation'}
                          >
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              style={{
                                stroke: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                                strokeWidth: '1.5',
                                strokeLinecap: 'round',
                                strokeLinejoin: 'round',
                                opacity: topPanelOpen === 'files' ? 1 : 0.7,
                              }}
                            >
                              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                            </svg>
                            {hasFiles && (
                              <div style={{
                                position: 'absolute',
                                top: '4px',
                                right: '4px',
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                backgroundColor: theme === 'dark' ? '#60A5FA' : '#3B82F6',
                                border: `2px solid ${theme === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(250, 248, 245, 0.95)'}`,
                                boxSizing: 'border-box',
                                pointerEvents: 'none',
                                boxShadow: theme === 'dark'
                                  ? '0 0 0 1px rgba(96, 165, 250, 0.3)'
                                  : '0 0 0 1px rgba(59, 130, 246, 0.3)',
                              }} />
                            )}
                          </button>
                        </div>
                        
                        {/* Expandable Panels */}
                        {topPanelOpen === 'reviews' && (
                          <ReviewsPanel
                            theme={theme}
                            onClose={() => setTopPanelOpen(null)}
                            reviewsLoading={reviewsLoading}
                            reviewsError={reviewsError}
                            reviews={reviews}
                          />
                        )}
                        
                        {topPanelOpen === 'notes' && (
                          <NotesPanel
                            theme={theme}
                            onClose={() => setTopPanelOpen(null)}
                            notesLoading={notesLoading}
                            notesError={notesError}
                            notes={notes}
                            formatDateTimeET={formatDateTimeET}
                          />
                        )}

                        {/* Links Section - FEATURE GATED */}
                        {OWNER_FEATURES.links && links.length > 0 && (
                          <LinksPanel theme={theme} links={links} />
                        )}
                        
                        {/* DOCUMENT SIGNAL: Strong Horizontal Rule (Quiet but Intentional) */}
                        {/* Note: Identity metadata (Guest Name, Reservation #, Channel, Dates, Nights, Guests) is now in modal header only */}
                        <div style={{
                          ...styles.folioSheetDividerStrong,
                          backgroundColor: theme === 'dark' 
                            ? 'rgba(255, 255, 255, 0.15)' 
                            : 'rgba(200, 195, 188, 0.4)', // Stronger rule (document signal)
                          marginTop: '0px', // Tight spacing - no excess whitespace
                          marginBottom: '24px', // Reduced from 32px for tighter rhythm
                        }} />

                        {/* OWNER STAY CARD: Show for true OwnerStay reservations (no financials) */}
                        {isOwnerStay && !isMobile && (
                          <OwnerStaySection theme={theme} variant="desktop" />
                        )}

                        {/* Files Panel (inline, matching Reviews/Notes pattern) */}
                        {topPanelOpen === 'files' && (
                          <FilesPanel
                            theme={theme}
                            onClose={() => setTopPanelOpen(null)}
                            filesLoading={filesLoading}
                            filesError={filesError}
                            files={files}
                            onRetry={refreshFiles}
                            formatDateTimeET={formatDateTimeET}
                          />
                        )}

                        {/* TWO-COLUMN FINANCIAL LAYOUT: Owner Payout (Primary) | Guest Paid (Reference) */}
                        {/* Desktop only - mobile uses FinancialDetailsModal */}
                        {/* Only show if NOT OwnerStay (OwnerStay shows special card above) */}
                        {!isOwnerStay && !isMobile && (
                          <BookingFinancialFolio
                            theme={theme}
                            styles={styles}
                            hostPayout={hostPayout}
                            guestPaid={guestPaid}
                            channelFeeLineItems={channelFeeLineItems}
                            detailData={detailData}
                            booking={booking}
                            persistedSummary={persistedSummary}
                            isLegacyVRBOiCal={persistedSummary?.tourist_tax?.is_legacy_vrbo_ical ?? checkLegacyVRBOiCal(resolveCanonical(detailData, booking))}
                            ownerPayoutLabelNode={(() => {
                              const canonical = resolveCanonical(detailData, booking);
                              const isLegacyVRBOiCal = persistedSummary?.tourist_tax?.is_legacy_vrbo_ical ?? checkLegacyVRBOiCal(canonical);
                              const isVRBONonICalComputed = checkVRBONonICalComputed(canonical);
                              if (isLegacyVRBOiCal) {
                                return <VrboSection theme={theme} variant="legacyIcal" />;
                              }
                              if (isVRBONonICalComputed) {
                                return <VrboSection theme={theme} variant="prestay" hostPayout={hostPayout} />;
                              }
                              const isCLCLease = checkCLCLease(canonical);
                              if (isCLCLease) {
                                return <ClcLeaseSection theme={theme} hostPayout={hostPayout} />;
                              }
                              const isBookingEngine = checkBookingEngine(canonical);
                              const isDirect = checkDirect(canonical);
                              const isHomeAway = checkHomeAway(canonical);
                              if (isBookingEngine || isDirect || isHomeAway) {
                                return <BookingEngineSection theme={theme} hostPayout={hostPayout} />;
                              }
                              const isAirbnb = canonical?.master_channel_key === 'airbnb';
                              if (isAirbnb) {
                                return <AirbnbSection theme={theme} hostPayout={hostPayout} />;
                              }
                              return <DefaultPayoutSection theme={theme} hostPayout={hostPayout} />;
                            })()}
                            handleOwnerPayoutTooltip={handleOwnerPayoutTooltip}
                            hideTooltip={hideTooltip}
                            showTooltip={showTooltip}
                            formatCurrencyRefined={formatCurrencyRefined}
                            formatFinanceValue={formatFinanceValue}
                            formatDateOnlyET={formatDateOnlyET}
                            resolveCanonical={resolveCanonical}
                            checkVRBONonICalComputed={checkVRBONonICalComputed}
                            checkCLCLease={checkCLCLease}
                            checkBookingEngine={checkBookingEngine}
                            checkLeaseStrict={checkLeaseStrict}
                            checkCLC={checkCLC}
                          />
                        )}

                        {/* OWNER REVENUE SECTION: Below two-column layout */}
                        {!isOwnerStay && !isMobile && (() => {
                          // Persisted-only modal cutover: Uses financials_persisted_summary - no canonical/snapshot fallback
                          // Persisted data already extracted above - use persistedAvailable and persistedSummary
                          if (!persistedAvailable || !persistedSummary) {
                            return null;
                          }
                          
                          // Debug: Log persisted data for troubleshooting (always log if ?debug=1)
                          const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
                          const debugMode = urlParams?.get('debug') === '1';
                          if (debugMode) {
                            console.log('[BookingDetailModal] Owner Revenue Debug:', {
                              has_detailData: !!detailData,
                              persisted_available: persistedAvailable,
                              persisted_summary: persistedSummary,
                            });
                          }
                          
                          // Persisted-only modal cutover: Use financials_persisted_summary for owner revenue section
                          // All values come from persisted summary - no fallback to snapshot/canonical
                          const ownerPayoutTotalRaw = persistedSummary?.owner_payout?.owner_payout_total;
                          const ownerPayoutTotal = ownerPayoutTotalRaw != null ? parseFloat(ownerPayoutTotalRaw) : null;
                          const ownerPayoutStatus = persistedSummary?.owner_payout?.owner_payout_status;
                          const isPayoutUnknown = ownerPayoutStatus === 'unknown_missing_hostaway' || ownerPayoutTotal === null;
                          
                          const touristTaxAmount = parseFloat(persistedSummary?.tourist_tax?.amount || 0);
                          const touristTaxLabel = persistedSummary?.tourist_tax?.label || 'Owner remits';
                          // Data-driven: Use tourist_tax_to_subtract from API (computed by backend)
                          // For Airbnb, tourist_tax_to_subtract = 0 (Airbnb remits taxes, so owner doesn't remit)
                          // Legacy VRBO iCal: channel remits; never subtract. UI must NOT show tourist tax subtraction row.
                          const touristTaxToSubtract = persistedSummary?.tourist_tax?.tourist_tax_to_subtract !== undefined
                            ? parseFloat(persistedSummary.tourist_tax.tourist_tax_to_subtract || 0)
                            : (persistedSummary?.tourist_tax?.owner_remits ? touristTaxAmount : 0);
                          const canonicalRev = resolveCanonical(detailData, booking);
                          const isLegacyVRBOiCalRev = persistedSummary?.tourist_tax?.is_legacy_vrbo_ical ?? checkLegacyVRBOiCal(canonicalRev);
                          const clcFeeAmount = persistedSummary?.clc_fee?.amount;
                          const clcPercentApplied = persistedSummary?.clc_fee?.percent;
                          const ownerRentalRevenue = persistedSummary?.owner_revenue?.amount;
                          
                          // Only show if we have meaningful data
                          // Always show if we have payout OR if we have percent (even if fee is 0.00)
                          // GOLD STANDARD: Show section even if payout is unknown (to display "Unknown" state)
                          const hasValidPayout = ownerPayoutTotal != null && ownerPayoutTotal !== 0;
                          const hasValidTax = touristTaxAmount != null && touristTaxAmount !== 0;
                          const hasValidFee = clcFeeAmount != null && clcFeeAmount !== 0;
                          const hasPercent = clcPercentApplied != null;
                          // Show section if we have payout, tax, fee, OR percent (percent means fee should be shown even if 0.00)
                          // OR if payout is unknown (to show "Unknown" state)
                          if (!hasValidPayout && !hasValidTax && !hasValidFee && !hasPercent && !isPayoutUnknown) {
                            return null;
                          }
                          
                          return (
                            <>
                              {/* Full-width divider */}
                              <div 
                                data-bookingdetail-modal-root="owner-revenue-section"
                                style={{
                                width: '100%',
                                height: '1px',
                                backgroundColor: theme === 'dark' 
                                  ? 'rgba(255, 255, 255, 0.12)' 
                                  : 'rgba(200, 195, 188, 0.25)',
                                marginTop: '32px',
                                marginBottom: '24px',
                              }} />
                              
                              {/* Owner Revenue Calculation Ladder */}
                              <div style={{
                                width: '100%',
                                paddingTop: '0',
                              }}>
                                <div style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '12px',
                                }}>
                                  {/* Owner Payout */}
                                  <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                  }}>
                                    <span style={{
                                      fontSize: '16px',
                                      fontWeight: 'var(--font-weight-normal)',
                                      color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
                                    }}>
                                      Owner Payout
                                    </span>
                                    <span style={{
                                      fontSize: '16px',
                                      fontWeight: 'var(--font-weight-normal)',
                                      color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
                                    }}>
                                      {formatCurrencyRefined(ownerPayoutTotal)}
                                    </span>
                                  </div>
                                  
                                  {/* Tourist Tax (if applicable and owner remits) */}
                                  {/* For Airbnb, tourist_tax_to_subtract = 0. Legacy VRBO iCal: channel remits; never show subtraction row. */}
                                  {!isLegacyVRBOiCalRev && touristTaxToSubtract > 0 && (
                                    <div style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                    }}>
                                      <span style={{
                                        fontSize: '14px',
                                        fontWeight: 'var(--font-weight-normal)',
                                        color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                      }}>
                                        <span>–</span>
                                        <span>Tourist Tax</span>
                                        <span style={{
                                          fontSize: '11px',
                                          fontWeight: 'var(--font-weight-normal)',
                                          color: theme === 'dark' ? 'var(--color-text-tertiary-dark)' : 'var(--color-text-tertiary)',
                                          fontStyle: 'italic',
                                        }}>
                                          ({touristTaxLabel})
                                        </span>
                                      </span>
                                      <span style={{
                                        fontSize: '14px',
                                        fontWeight: 'var(--font-weight-normal)',
                                        color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                                      }}>
                                        {`–${formatCurrencyRefined(touristTaxToSubtract)}`}
                                      </span>
                                    </div>
                                  )}
                                  
                                  {/* Damage Deposit (lease only) – deducted from payout to compute revenue */}
                                  {(() => {
                                    const damageDepositDeduction = persistedSummary?.damage_deposit_deduction;
                                    const damageDepositAmount = damageDepositDeduction?.amount ?? 0;
                                    if (damageDepositAmount <= 0) return null;
                                    return (
                                      <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                      }}>
                                        <span style={{
                                          fontSize: '14px',
                                          fontWeight: 'var(--font-weight-normal)',
                                          color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '6px',
                                        }}>
                                          <span>–</span>
                                          <span>Damage Deposit</span>
                                          <span style={{
                                            fontSize: '11px',
                                            fontWeight: 'var(--font-weight-normal)',
                                            color: theme === 'dark' ? 'var(--color-text-tertiary-dark)' : 'var(--color-text-tertiary)',
                                            fontStyle: 'italic',
                                          }}>
                                            (Refundable)
                                          </span>
                                        </span>
                                        <span style={{
                                          fontSize: '14px',
                                          fontWeight: 'var(--font-weight-normal)',
                                          color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                                        }}>
                                          {`–${formatCurrencyRefined(damageDepositAmount)}`}
                                        </span>
                                      </div>
                                    );
                                  })()}
                                  
                                  {/* CLC Fee – always shown when section is visible; $ when available (0.00 ok), % when available */}
                                  <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                  }}>
                                    <span style={{
                                      fontSize: '14px',
                                      fontWeight: 'var(--font-weight-normal)',
                                      color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                    }}>
                                      <span>–</span>
                                      <span>CLC Fee</span>
                                      {clcPercentApplied != null && formatClcFeePercentForDisplay(clcPercentApplied) && (
                                        <span style={{
                                          fontSize: '12px',
                                          fontWeight: 'var(--font-weight-normal)',
                                          color: theme === 'dark' ? 'var(--color-text-tertiary-dark)' : 'var(--color-text-tertiary)',
                                        }}>
                                          ({formatClcFeePercentForDisplay(clcPercentApplied)})
                                        </span>
                                      )}
                                    </span>
                                    <span style={{
                                      fontSize: '14px',
                                      fontWeight: 'var(--font-weight-normal)',
                                      color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                                    }}>
                                      {clcFeeAmount !== null ? `–${formatCurrencyRefined(clcFeeAmount)}` : '—'}
                                    </span>
                                  </div>
                                  
                                  {/* Divider */}
                                  <div style={{
                                    width: '100%',
                                    height: '1px',
                                    backgroundColor: theme === 'dark' 
                                      ? 'rgba(255, 255, 255, 0.12)' 
                                      : 'rgba(200, 195, 188, 0.25)',
                                    marginTop: '4px',
                                    marginBottom: '4px',
                                  }} />
                                  
                                  {/* Owner Revenue (Total) */}
                                  <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                  }}>
                                    <span style={{
                                      fontSize: '18px',
                                      fontWeight: 'var(--font-weight-semibold)',
                                      color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
                                    }}>
                                      Owner Revenue
                                    </span>
                                    <span style={{
                                      fontSize: '18px',
                                      fontWeight: 'var(--font-weight-semibold)',
                                      color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
                                    }}>
                                      {isPayoutUnknown 
                                        ? 'Unknown' 
                                        : (ownerRentalRevenue !== null ? formatCurrencyRefined(ownerRentalRevenue) : '—')
                                      }
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </>
                          );
                        })()}

                        {/* Mobile: Owner Stay Card (View Financials removed — financials are inline above) */}
                        {isMobile && isOwnerStay && (
                          <OwnerStaySection theme={theme} variant="mobile" />
                        )}
                        {false && isMobile && !isOwnerStay && (
                          <div 
                            style={{
                              padding: 'var(--spacing-4) 0',
                              borderTop: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(200, 195, 188, 0.2)'}`,
                              marginTop: 'var(--spacing-4)',
                            }}
                            data-bookingdetail-financials-cta
                          >
                            <button
                              type="button"
                              data-bookingdetail-view-financials
                              onClick={() => setIsFinancialModalOpen(true)}
                              style={{
                                width: '100%',
                                padding: 'var(--spacing-3) var(--spacing-4)',
                                minHeight: '44px',
                                backgroundColor: 'var(--color-brand-primary, #2563eb)',
                                color: 'white',
                                border: 'none',
                                borderRadius: 'var(--radius-md)',
                                fontSize: 'var(--font-size-base)',
                                fontWeight: 'var(--font-weight-semibold)',
                                cursor: 'pointer',
                                transition: 'background-color 120ms ease-out',
                                touchAction: 'manipulation',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--color-brand-primary-hover, #1d4ed8)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--color-brand-primary, #2563eb)';
                              }}
                              aria-label="View financial details"
                            >
                              View Financials
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}
        </div>
      </div>
    </>
  );

  // RENDER PATH: Mobile => full-screen modal (no drawer, no scrolling underneath); desktop => portal + inline folio.
  if (isMobile) {
    const mobileFullScreenModal = (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        style={{
          ...styles.mobileFullScreenBackdrop,
          backgroundColor: theme === 'dark' ? 'rgba(0,0,0,0.85)' : 'rgba(0,0,0,0.6)',
        }}
        onClick={(e) => e.target === e.currentTarget && handleBackdropClick(e)}
      >
        <div
          style={{
            ...styles.mobileFullScreenPanel,
            backgroundColor: theme === 'dark' ? 'var(--color-bg-primary-dark)' : 'var(--color-bg-primary)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {ModalContent}
        </div>
      </div>
    );
    return typeof document !== 'undefined' ? createPortal(mobileFullScreenModal, document.body) : null;
  }

  // Render modal via portal to document.body to escape stacking contexts
  const modalElement = (
    <BookingDetailModalShell
      isOpen={isOpen}
      isMobile={false}
      onClose={onClose}
      onBackdropClick={handleBackdropClick}
      theme={theme}
      styles={styles}
      accentColor={accentColor}
      disableDrag={false}
    >
      {ModalContent}
    </BookingDetailModalShell>
  );

  // Use portal to render modal and tooltip at document.body level, escaping any parent stacking contexts
  // Tooltip must be portaled separately to escape modal's transform context
  return (
    <>
      {tooltipState.show && typeof document !== 'undefined' && createPortal(
        <TooltipPortal
          show={tooltipState.show}
          content={tooltipState.content}
          x={tooltipState.x}
          y={tooltipState.y}
          positionAbove={tooltipState.positionAbove}
          theme={theme}
          onMouseEnter={cancelHideTooltip}
          onMouseLeave={hideTooltip}
        />,
        document.body
      )}
      {createPortal(modalElement, document.body)}
      
      {/* FinancialDetailsModal only renders on mobile (handled internally) */}
      {bookingForFinancialModal && (
        <FinancialDetailsModal
          isOpen={isFinancialModalOpen}
          onClose={() => setIsFinancialModalOpen(false)}
          booking={bookingForFinancialModal}
          isMobile={isMobile}
          onRetryFetch={() => refreshDetailData()}
        />
      )}
    </>
  );
}

const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0, // top: 0, left: 0, right: 0, bottom: 0
    backgroundColor: 'rgba(0, 0, 0, 0.6)', // Darker backdrop (0.6 vs 0.5) for better depth
    zIndex: 10000, // Above owner layout header (9999) so modal and close button are not cut off
    overflowY: 'auto', // Allow backdrop to scroll if modal is taller than viewport
    // No padding - true centering via transform on modal
  },
  // Mobile: full-screen takeover (no drawer, no scroll underneath).
  // z-index above app shell (9999) so modal header and close button are visible.
  mobileFullScreenBackdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 10000,
    overflow: 'hidden', // No scrolling underneath
    display: 'flex',
    flexDirection: 'column',
  },
  mobileFullScreenPanel: {
    position: 'fixed',
    inset: 0,
    zIndex: 10001,
    overflow: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
    display: 'flex',
    flexDirection: 'column',
    paddingTop: 'env(safe-area-inset-top, 0)', // Clear notch so header/close visible
    paddingBottom: 'env(safe-area-inset-bottom, 0)',
  },
  modal: {
    backgroundColor: 'var(--color-bg-primary)',
    borderRadius: '16px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', // Multi-layer shadow for depth
    border: '1px solid var(--color-border-light)', // Subtle border for edge separation
    width: 'min(920px, calc(100vw - 80px))', // 920px optimal, 80px total gutter (40px each side)
    maxHeight: 'calc(100vh - 120px)', // 80px top safe area + 40px bottom safe area
    overflow: 'hidden', // Let content scroll internally
    position: 'fixed', // True viewport centering
    display: 'flex',
    flexDirection: 'column',
    zIndex: 10001, // Above backdrop (10000) - modal content on top
    // top: 50%, left: 50%, transform: translate(-50%, -50%) applied inline for theme support
  },
  header: {
    padding: '24px 32px 20px 32px', // 24px top, 20px bottom, 32px horizontal (reduced from 32px vertical for density)
    borderBottom: '1px solid var(--color-border-light)',
    flexShrink: 0,
    margin: 0,
    backgroundColor: 'inherit',
    position: 'sticky', // Sticky header within modal
    top: 0,
    zIndex: 1, // Above scrolling content
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '24px',
    margin: 0,
  },
  headerLeft: {
    flex: 1,
    minWidth: 0, // Allow flex shrinking
  },
  headerTitleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px', // 16px gap between monogram and title stack
  },
  monogram: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 'var(--font-weight-medium)',
    flexShrink: 0,
    backgroundColor: 'var(--color-bg-secondary)',
    color: 'var(--color-text-primary)',
  },
  headerTitleStack: {
    flex: 1,
    minWidth: 0, // Allow flex shrinking
  },
  title: {
    fontSize: '24px', // 24px - dominant emotional anchor (spec: 24px semibold)
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    margin: 0,
    marginBottom: '12px', // 12px spacing (spec: title to meta)
    lineHeight: '1.2', // Tighter line height for impact (spec: 1.2)
  },
  headerPrimaryLine: {
    fontSize: '18px',
    fontWeight: 'var(--font-weight-medium)',
    lineHeight: '1.4',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0',
  },
  headerSecondaryLine: {
    fontSize: '14px',
    fontWeight: 'var(--font-weight-normal)',
    lineHeight: '1.5',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0',
  },
  headerMeta: {
    display: 'flex',
    gap: '8px', // 8px spacing between badges (spec: related items)
    alignItems: 'center',
    marginTop: '8px', // 8px spacing after meta line (spec: related items)
    marginBottom: 0, // No bottom margin
    flexWrap: 'wrap',
  },
  headerMetaLine: {
    fontSize: '15px', // 15px - supporting info (spec: 15px between base and large)
    color: 'var(--color-text-secondary)', // Secondary color (60% opacity equivalent)
    fontWeight: 'var(--font-weight-normal)', // Regular weight (spec: 400)
    lineHeight: '1.4', // Spec: 1.4
    marginTop: 0, // No margin top - flows from title
    marginBottom: 0, // No margin bottom
  },
  subtitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px', // 8px gap between parts
    flexWrap: 'wrap',
  },
  subtitleWithDot: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px', // 6px gap between dot and channel text
  },
  channelDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
    display: 'inline-block',
  },
  channelBadge: {
    fontSize: '12px', // 12px - minimal, recedes (spec: 12px)
    color: 'var(--color-text-secondary)',
    padding: '4px 8px', // 4px vertical, 8px horizontal (spec: tight coupling)
    borderRadius: 'var(--radius-sm)',
    backgroundColor: 'transparent', // No background - text only, minimal
  },
  headerActions: {
    display: 'flex',
    gap: '8px', // 8px spacing between action buttons (spec: related items)
    alignItems: 'center',
    flexShrink: 0,
  },
  actionButton: {
    padding: 0, // No padding - icon fills hit area
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-secondary)', // Tertiary - light, not competing
    backgroundColor: 'transparent', // No background - minimal
    border: 'none', // No border - minimal
    borderRadius: 'var(--radius-md)',
    cursor: 'not-allowed', // Disabled state
    transition: 'var(--transition-base)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px', // 36px hit area (spec: 36×36px)
    height: '36px', // 36px hit area (spec: 36×36px)
    opacity: 0.6, // Light - tertiary, not competing
  },
  actionButtonRefined: {
    // Hover/focus states handled via inline event handlers (onMouseEnter, onFocus)
    // React inline styles don't support :hover/:focus pseudo-classes
  },
  closeButton: {
    width: '32px',
    height: '32px',
    borderRadius: 'var(--radius-full)',
    border: 'none',
    backgroundColor: 'var(--color-bg-tertiary)',
    color: 'var(--color-text-primary)',
    fontSize: 'var(--font-size-2xl)',
    fontWeight: 'var(--font-weight-bold)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    transition: 'var(--transition-base)',
  },
  content: {
    padding: '0px 32px 24px 32px', // 0px top (icons sit close to header), 24px bottom, 32px horizontal
    flex: 1,
    overflowY: 'auto', // Internal scroll
    minHeight: 0, // Allow flex shrinking
    margin: 0,
    backgroundColor: 'inherit',
  },
  twoColumnLayout: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr', // Desktop: 2 columns
    gap: '40px', // 40px column gap (spec: major separation)
    // Mobile stacking handled by isMobile check in component
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px', // 32px section gap (spec: section separation)
    minWidth: 0, // Allow flex shrinking
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px', // 32px section gap (spec: section separation)
    minWidth: 0, // Allow flex shrinking
  },
  section: {
    marginBottom: 0, // Use gap from parent
    // NO background, NO border, NO padding, NO radius - flat sections only
    backgroundColor: 'transparent',
    padding: 0,
    borderRadius: 0,
    border: 'none',
  },
  sectionTitle: {
    fontSize: '14px', // 14px - section headings (spec: smaller than content, subordinate)
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    margin: 0,
    marginTop: '32px', // 32px section gap (first section: 0, handled in component)
    marginBottom: '12px', // 12px title to content (spec: content proximity)
    paddingBottom: 0,
    borderBottom: 'none',
    textTransform: 'none', // Sentence case (spec: not uppercase)
  },
  compactGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px', // 20px between items (spec: content separation)
  },
  compactItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px', // 4px label to value (spec: tight coupling)
  },
  compactLabel: {
    fontSize: '12px', // 12px - labels (spec: 12px uppercase)
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: 'var(--letter-spacing-wide)',
  },
  compactValue: {
    fontSize: '15px', // 15px - values (spec: 15px regular)
    color: 'var(--color-text-primary)',
    fontWeight: 'var(--font-weight-normal)',
  },
  emptyValue: {
    fontSize: 'var(--font-size-sm)', // 14px
    color: 'var(--color-text-secondary)',
    fontStyle: 'italic',
  },
  emptyStateMessage: {
    marginTop: '20px', // 20px spacing (spec: content separation)
    padding: 0, // No padding - flat, not card-like
    backgroundColor: 'transparent', // No background - flat
    borderRadius: 0, // No radius - flat
    border: 'none', // No border - flat
  },
  emptyStateText: {
    fontSize: '15px', // 15px - same as values (spec: readable but not prominent)
    color: 'var(--color-text-secondary)',
    margin: 0,
    lineHeight: '1.5', // Standard line height
    fontWeight: 'var(--font-weight-normal)',
  },
  financialGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px', // 20px between financial items (spec: content separation)
  },
  financialItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  financialLabel: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
  },
  financialTotal: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statusRow: {
    display: 'flex',
    gap: 'var(--spacing-2)',
    marginTop: 'var(--spacing-3)',
    flexWrap: 'wrap',
  },
  paymentStatus: {
    fontSize: 'var(--font-size-xs)',
    padding: 'var(--spacing-2)',
    borderRadius: 'var(--radius-sm)',
    marginTop: 'var(--spacing-3)',
  },
  checklist: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-2)',
  },
  checklistItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    fontSize: 'var(--font-size-sm)',
  },
  notes: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-primary)',
    lineHeight: 'var(--line-height-relaxed)',
    padding: 'var(--spacing-3)',
    backgroundColor: 'var(--color-bg-secondary)',
    borderRadius: 'var(--radius-md)',
  },
  taxStatusContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-2)',
  },
  taxMessage: {
    padding: 'var(--spacing-3)',
    backgroundColor: 'var(--color-status-warning-bg, #fef3c7)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-status-warning-base, #f59e0b)',
  },
  taxMessageText: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-status-warning-text, #92400e)',
    margin: 0,
    marginBottom: 'var(--spacing-2)',
    lineHeight: '1.5',
  },
  taxLinkButton: {
    padding: 'var(--spacing-2) var(--spacing-3)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-primary-base)',
    backgroundColor: 'transparent',
    border: '1px solid var(--color-primary-base)',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    transition: 'var(--transition-base)',
    textDecoration: 'none',
  },
  validationTable: {
    width: '100%',
    overflowX: 'auto',
  },
  validationTableInner: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 'var(--font-size-sm)',
  },
  validationTableHeader: {
    padding: '8px 12px',
    textAlign: 'left',
    fontWeight: 'var(--font-weight-semibold)',
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid var(--color-border-light)',
  },
  validationTableRow: {
    borderBottom: '1px solid var(--color-border-light)',
  },
  validationTableLabel: {
    padding: '8px 12px',
    fontWeight: 'var(--font-weight-medium)',
    verticalAlign: 'top',
  },
  validationTableValue: {
    padding: '8px 12px',
    textAlign: 'right',
    fontFamily: 'inherit',
  },
  validationTableRawKey: {
    padding: '8px 12px',
    fontSize: '10px',
    fontFamily: 'monospace',
  },
  financeDebugPanel: {
    fontSize: '12px',
  },
  // Full Modal Folio Viewer - Financial folio owns entire modal canvas
  folioViewerContainer: {
    width: '100%',
    maxWidth: '1000px', // Intentional modal width (900-1000px per design spec)
    margin: '0 auto', // Center the folio in modal
  },
  // Luxury Hotel Folio Sheet Styles (Option B: Modern Luxury Folio - Two-Column)
  folioSheetContainer: {
    padding: '0px 32px 40px 32px', // 0px top (icons sit close to header), 40px bottom, 32px horizontal
    borderRadius: '8px',
    border: 'none', // No border - modal background is folio surface
    boxShadow: 'none', // No shadow - modal background is folio surface
    marginBottom: '0',
    width: '100%',
  },
  // Two-Column Financial Layout
  folioTwoColumnLayout: {
    display: 'grid',
    gridTemplateColumns: '60% 1px 40%', // 60/40 split with 1px separator
    gap: '0',
    alignItems: 'stretch', // Stretch columns to equal height for bottom anchoring
    marginTop: '0', // Tight spacing - divider already provides separation
  },
  folioColumnOwner: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
    paddingRight: '32px', // Increased from 24px for more whitespace
  },
  folioColumnGuest: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
    paddingLeft: '32px', // Increased from 24px for more whitespace
  },
  folioColumnSeparator: {
    width: '1px',
    height: '100%',
    minHeight: '200px',
    alignSelf: 'stretch',
  },
  folioColumnTitle: {
    fontSize: '18px',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-text-primary)',
    marginBottom: '20px', // Increased for breathing room
    letterSpacing: '0.02em', // Refinement
    lineHeight: '1.3', // Tighter for titles
  },
  folioColumnTitleSecondary: {
    fontSize: '14px',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-secondary)',
    marginBottom: '20px', // Increased for breathing room
    letterSpacing: '0.02em', // Refinement
    lineHeight: '1.3', // Tighter for titles
    opacity: 0.8, // Muted for secondary
  },
  // Owner Payout Lockup (Statement Moment)
  folioColumnLockup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0', // Controlled spacing via margins
    marginTop: '0',
    marginBottom: '24px', // Restored spacing rhythm (small gap label→amount, larger pause after)
    paddingTop: '0',
  },
  folioColumnLockupLabel: {
    fontSize: '15px',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-primary)',
    letterSpacing: '0.02em', // Refinement
    lineHeight: '1.5', // Generous for readability
    marginBottom: '4px', // Small gap before amount
  },
  folioColumnLockupAmount: {
    fontSize: '22px',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-text-primary)',
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '-0.01em', // Tight, precise feel for numbers
    lineHeight: '1.2', // Tighter for display numbers
    marginTop: '0', // Amount is isolated on its own line
    marginBottom: '0',
  },
  folioColumnLockupAmountStatement: {
    fontSize: '32px', // Largest typography element in modal (statement moment)
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-text-primary)',
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '-0.02em', // Tighter for large display numbers
    lineHeight: '1.1', // Tighter for display numbers
    marginTop: '8px', // Space from label
    marginBottom: '8px', // Space before payout date subline
  },
  folioColumnLockupTiming: {
    fontSize: '13px',
    fontWeight: 'var(--font-weight-normal)',
    color: 'var(--color-text-secondary)',
    fontStyle: 'italic',
    marginTop: '4px',
    marginBottom: '0',
    opacity: 0.7, // Subtle/muted
  },
  folioColumnLockupPending: {
    fontSize: '14px',
    fontWeight: 'var(--font-weight-normal)',
    color: 'var(--color-text-secondary)',
    fontStyle: 'italic',
    marginTop: '0',
    marginBottom: '0',
  },
  folioColumnHeroRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '0',
    paddingTop: '4px', // Subtle top padding for emphasis
    paddingBottom: '20px', // Generous bottom padding for breathing room
  },
  // Guest Paid: Compressed Rhythm (Inline, Tighter Spacing)
  folioColumnHeroRowCompressed: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '0',
    paddingTop: '0', // No top padding (compressed)
    paddingBottom: '12px', // Tighter bottom spacing (compressed from 20px)
  },
  folioColumnHeroLabel: {
    fontSize: '15px',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-primary)',
    letterSpacing: '0.02em', // Refinement
    lineHeight: '1.5', // Generous for readability
  },
  folioColumnHeroAmount: {
    fontSize: '22px',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-text-primary)',
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '-0.01em', // Tight, precise feel for numbers
    lineHeight: '1.2', // Tighter for display numbers
  },
  folioColumnHeroPending: {
    fontSize: '14px',
    fontWeight: 'var(--font-weight-normal)',
    color: 'var(--color-text-secondary)',
    fontStyle: 'italic',
  },
  folioColumnHeroLabelSecondary: {
    fontSize: '14px',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-secondary)',
    letterSpacing: '0.02em', // Refinement
    lineHeight: '1.5', // Generous for readability
    opacity: 0.8, // Muted for secondary
  },
  folioColumnHeroAmountSecondary: {
    fontSize: '16px',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-secondary)',
    fontVariantNumeric: 'tabular-nums', // Precision alignment
    letterSpacing: '-0.01em', // Tight, precise feel for numbers
    lineHeight: '1.4',
    opacity: 0.9, // Muted for secondary
  },
  folioColumnBreakdownTitle: {
    fontSize: '15px',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    marginTop: '4px', // Subtle top spacing
    marginBottom: '16px', // Increased for breathing room
    letterSpacing: '0.02em', // Refinement
    lineHeight: '1.4',
  },
  folioColumnBreakdownTitleSecondary: {
    fontSize: '13px',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-secondary)',
    marginTop: '4px', // Subtle top spacing
    marginBottom: '12px', // Tighter spacing (compressed from 16px)
    letterSpacing: '0.02em', // Refinement
    lineHeight: '1.4',
    opacity: 0.8, // Muted for secondary
  },
  folioColumnLedger: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
  },
  folioColumnLedgerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    padding: '10px 0', // Increased from 8px for better rhythm
    minHeight: '40px', // Consistent row height for alignment
  },
  // Guest Paid Ledger: Compressed Rhythm
  folioColumnLedgerRowCompressed: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    padding: '8px 0', // Tighter spacing (compressed from 10px)
    minHeight: '36px', // Slightly smaller (compressed from 40px)
  },
  folioColumnLedgerLabel: {
    fontSize: '15px',
    fontWeight: 'var(--font-weight-normal)',
    color: 'var(--color-text-secondary)',
    lineHeight: '1.5', // Generous for readability
    letterSpacing: '0.01em', // Subtle refinement
  },
  folioColumnLedgerValue: {
    fontSize: '17px',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-primary)',
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums', // Precision alignment
    letterSpacing: '-0.01em', // Tight, precise feel for numbers
    lineHeight: '1.4',
  },
  folioColumnLedgerLabelSecondary: {
    fontSize: '13px',
    fontWeight: 'var(--font-weight-normal)',
    color: 'var(--color-text-secondary)',
    lineHeight: '1.5', // Generous for readability
    letterSpacing: '0.01em', // Subtle refinement
    opacity: 0.7, // Muted for secondary
  },
  folioColumnLedgerValueSecondary: {
    fontSize: '14px',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-secondary)',
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums', // Precision alignment
    letterSpacing: '-0.01em', // Tight, precise feel for numbers
    lineHeight: '1.4',
    opacity: 0.8, // Muted for secondary
  },
  folioColumnDivider: {
    height: '1px',
    width: '100%',
    margin: '0',
    backgroundColor: 'rgba(200, 195, 188, 0.15)', // Very subtle, warm tone
  },
  folioColumnDividerThick: {
    height: '2px', // Thicker rule above totals
    width: '100%',
    margin: '0',
    backgroundColor: 'rgba(200, 195, 188, 0.25)', // Slightly more visible
  },
  folioSheetHeader: {
    marginBottom: '24px', // Increased from 16px for breathing room
    display: 'flex',
    flexDirection: 'column',
    gap: '6px', // Increased from 4px for better spacing
  },
  folioSheetMetadataLine: {
    fontSize: '13px',
    color: 'var(--color-text-secondary)',
    lineHeight: '1.5', // Generous for readability
    fontWeight: 'var(--font-weight-normal)',
    letterSpacing: '0.01em', // Subtle refinement
  },
  folioSheetTitle: {
    fontSize: '16px',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    marginBottom: '8px',
    letterSpacing: '0.02em',
  },
  folioSheetContext: {
    fontSize: '13px',
    color: 'var(--color-text-secondary)',
    lineHeight: '1.4',
  },
  folioSheetDivider: {
    height: '1px',
    width: '100%',
    backgroundColor: 'rgba(200, 195, 188, 0.15)', // Very subtle, warm tone
    margin: '20px 0', // Increased from 16px for breathing room
  },
  // Document Signal: Strong Horizontal Rule (Quiet but Intentional)
  folioSheetDividerStrong: {
    height: '2px', // Stronger rule (document signal)
    width: '100%',
    margin: '0',
  },
  folioSheetHero: {
    padding: '20px 0',
    marginBottom: '0',
  },
  folioSheetHeroRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
  },
  folioSheetHeroLabel: {
    fontSize: '15px',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-primary)',
    letterSpacing: '0.02em',
  },
  folioSheetHeroAmount: {
    fontSize: '20px',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-text-primary)',
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '-0.01em',
  },
  folioSheetHeroPending: {
    fontSize: '14px',
    fontWeight: 'var(--font-weight-normal)',
    color: 'var(--color-text-secondary)',
    fontStyle: 'italic',
  },
  folioSheetSectionTitle: {
    fontSize: '14px',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    marginBottom: '0',
    letterSpacing: '0.02em',
  },
  folioSheetSectionTitleSecondary: {
    fontSize: '14px',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-secondary)',
    marginBottom: '0',
    letterSpacing: '0.02em',
    opacity: 0.8,
  },
  folioSheetLedger: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
  },
  folioSheetLedgerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    padding: '8px 0',
  },
  folioSheetLedgerLabel: {
    fontSize: '14px',
    fontWeight: 'var(--font-weight-normal)',
    color: 'var(--color-text-secondary)',
  },
  folioSheetLedgerValue: {
    fontSize: '15px',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-primary)',
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  },
  folioSheetLedgerLabelSecondary: {
    fontSize: '14px',
    fontWeight: 'var(--font-weight-normal)',
    color: 'var(--color-text-secondary)',
    opacity: 0.7,
  },
  folioSheetLedgerValueSecondary: {
    fontSize: '15px',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-secondary)',
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    opacity: 0.8,
  },
  folioSheetValidationSection: {
    marginTop: '24px',
    paddingTop: '16px',
    borderTop: '1px solid rgba(200, 195, 188, 0.15)',
  },
  folioSheetValidationToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 0',
    fontSize: '11px',
    fontWeight: 'var(--font-weight-normal)',
    border: 'none',
    borderRadius: '0',
    cursor: 'pointer',
    transition: 'var(--transition-base)',
    backgroundColor: 'transparent',
    color: 'var(--color-text-tertiary)',
    width: '100%',
    textAlign: 'left',
    opacity: 0.7,
  },
  folioSheetValidationToggleIcon: {
    fontSize: '10px',
    flexShrink: 0,
  },
  folioSheetValidationToggleText: {
    flex: 1,
  },
  folioInfoIcon: {
    fontSize: '12px',
    opacity: 0.6,
    cursor: 'help',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    transition: 'opacity 0.2s',
    pointerEvents: 'auto', // Ensure tooltip icon is interactive
    flexShrink: 0, // Prevent icon from shrinking
  },
  ownerStayActionWrap: {
    marginTop: '18px',
    paddingTop: '16px',
    borderTop: '1px solid rgba(148, 163, 184, 0.28)',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  ownerStayActionRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
  },
  ownerStaySecondaryAction: {
    minHeight: '40px',
    padding: '0 14px',
    border: '1px solid var(--color-border-medium, #d1d5db)',
    borderRadius: '8px',
    backgroundColor: 'var(--color-bg-primary, #ffffff)',
    color: 'var(--color-text-primary, #111827)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  ownerStayDangerAction: {
    minHeight: '40px',
    padding: '0 14px',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    backgroundColor: '#fef2f2',
    color: '#991b1b',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  ownerStayActionError: {
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #fecaca',
    backgroundColor: '#fef2f2',
    color: '#991b1b',
    fontSize: '13px',
    lineHeight: 1.4,
  },
  // Legacy styles (kept for backward compatibility if needed)
  financialSummaryContainer: {
    marginBottom: '24px',
  },
  financialSummaryGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
  },
  financialBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  financialBlockTitle: {
    fontSize: '14px',
    fontWeight: 'var(--font-weight-semibold)',
    margin: 0,
    marginBottom: '8px',
  },
  financialBlockContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  financialRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
  },
  financialRowTotal: {
    marginTop: '8px',
    paddingTop: '8px',
  },
  financialLabelTotal: {
    fontWeight: 'var(--font-weight-semibold)',
  },
  financialValueTotal: {
    fontSize: '15px',
    fontWeight: 'var(--font-weight-semibold)',
  },
  validationToggleButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    fontSize: '11px',
    fontWeight: 'var(--font-weight-normal)',
    border: '1px solid',
    borderColor: 'rgba(200, 195, 188, 0.2)',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    transition: 'var(--transition-base)',
    backgroundColor: 'transparent',
    color: 'var(--color-text-tertiary)',
    width: '100%',
    textAlign: 'left',
    opacity: 0.7,
  },
  validationToggleIcon: {
    fontSize: '10px',
    flexShrink: 0,
  },
  validationToggleText: {
    flex: 1,
  },
};
